/* ============================================================
   FILE: packages/volto-castle-theme/src/components/Footer/Footer.tsx
   ============================================================ */

import React from 'react';
import { Link } from 'react-router-dom';

type FooterLink = { label: string; href?: string };

type FooterLinkGroup = {
  title: string;
  links: FooterLink[];
};

const linkGroups: FooterLinkGroup[] = [
  {
    title: 'CONTENT & LAYOUT',
    links: [
      { label: 'Intuitive Content Management' },
      { label: 'Dynamic Layout Engine' },
      { label: 'Easy Theming' },
    ],
  },
  {
    title: 'SECURITY & QUALITY',
    links: [
      { label: 'High Security' },
      { label: 'Content Quality Assurance' },
      { label: 'Content Previews' },
    ],
  },
  {
    title: 'SEARCH & DISCOVERY',
    links: [
      { label: 'Comprehensive Built-in Search' },
      { label: 'Search Engine Optimization' },
      { label: 'A Viewer for All File Types' },
    ],
  },
  {
    title: 'INTEGRATIONS & EDITING',
    links: [
      { label: 'Superior Social Media Integration' },
      { label: 'Rich Content Editing' },
      { label: 'Intuitive Toolbar' },
    ],
  },
];

function slugify(input: string): string {
  return input
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function resolveFooterHref(link: FooterLink): string {
  if (link.href && link.href !== '#') return link.href;
  return `/services/${slugify(link.label)}`;
}

function isExternalHref(href: string): boolean {
  return /^https?:\/\//i.test(href);
}

const Footer: React.FC = () => {
  const year = new Date().getFullYear();

  return (
    <footer className="global-footer" role="contentinfo">
      <div className="footer-container">
        <div className="footer-grid">
          <div className="footer-brand">
            <div className="footer-brandHeader">
              <span
                className="footer-brandLogoSolid"
                role="img"
                aria-label="Castle CMS logo"
              />
              <span className="footer-brandName">CASTLE CMS</span>
            </div>

            <p className="footer-brandText">
              Sign up for a free demo today,
              <br />
              and see how CastleCMS can
              <br />
              benefit your business.
            </p>

            <div className="footer-brandMeta">
              <div className="footer-muted">(715) 869-3440, +88456595572</div>
              <div className="footer-muted">
                <a className="footer-email" href="mailto:example@mail.com">
                  example@mail.com
                </a>
                <span className="footer-dot">.</span>
                <a className="footer-email" href="mailto:info@wildcardcorp.com">
                  info@wildcardcorp.com
                </a>
              </div>
            </div>
          </div>

          {linkGroups.map((group) => (
            <nav key={group.title} className="footer-col" aria-label={group.title}>
              <h3 className="footer-heading">{group.title}</h3>
              <ul className="footer-list">
                {group.links.map((l) => {
                  const href = resolveFooterHref(l);

                  return (
                    <li key={l.label} className="footer-item">
                      {isExternalHref(href) ? (
                        <a
                          className="footer-link"
                          href={href}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {l.label}
                        </a>
                      ) : (
                        <Link className="footer-link" to={href}>
                          {l.label}
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            </nav>
          ))}
        </div>

        <div className="footer-divider" />

        <div className="footer-bottom">
          <span className="footer-bottomText">
            ALL RIGHTS RESERVED BY{' '}
            <a
              className="footer-bottomLink"
              href="https://wildcardcorp.com/"
              target="_blank"
              rel="noreferrer"
            >
              Wildcardcorp
            </a>{' '}
            {year}
          </span>
        </div>
      </div>

      <div className="footer-accent" aria-hidden="true" />
    </footer>
  );
};

export default Footer;